import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Dialog as HeadlessDialog } from "@headlessui/react";
import { useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { useAuth } from "../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../redux";
import { agentActions } from "../../redux/reducers/agentSlice";
import { useCommandPalette } from "./CommandPaletteContext";
import { CommandPaletteItem } from "./CommandPaletteItem";
import { searchRegistry } from "./searchRegistry";
import { SearchItem, SearchResult, SearchCategory } from "./types";

// Simple fuzzy match scoring
const fuzzyMatch = (text: string, query: string): number => {
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();

  if (lowerText === lowerQuery) return 100;
  if (lowerText.startsWith(lowerQuery)) return 80;
  if (lowerText.includes(lowerQuery)) return 60;

  const words = lowerText.split(/\s+/);
  if (words.some((word) => word.startsWith(lowerQuery))) return 50;

  return 0;
};

// Search through items
const searchItems = (items: SearchItem[], query: string, isAdmin: boolean): SearchResult[] => {
  const filteredItems = items.filter((item) => !item.adminOnly || isAdmin);

  if (!query.trim()) {
    // Show all items grouped by category when no query
    return filteredItems.slice(0, 20).map((item) => ({ ...item, score: 0 }));
  }

  return filteredItems
    .map((item) => {
      const titleScore = fuzzyMatch(item.title, query) * 1.5;
      const descScore = fuzzyMatch(item.description || "", query);
      const keywordScore = Math.max(...item.keywords.map((kw) => fuzzyMatch(kw, query)), 0);

      return {
        ...item,
        score: Math.max(titleScore, descScore, keywordScore),
      };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 15);
};

// Category order for display
const categoryOrder: SearchCategory[] = ["Agents", "Pages", "Agent Features", "Agent Settings", "Organization"];

export const CommandPalette: React.FC = () => {
  const { isOpen, close } = useCommandPalette();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Get agents from Redux
  const agentEntities = useAppSelector((state) => state.agent.entities);
  const agents = useMemo(() => Object.values(agentEntities), [agentEntities]);
  const agentsLoaded = useAppSelector((state) => state.agent.ids.length > 0 || state.agent.isLoading === false);

  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [pendingFeature, setPendingFeature] = useState<SearchResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Extract current agentId from URL if on an agent page
  const currentAgentId = useMemo(() => {
    const match = location.pathname.match(/\/agents\/([^/]+)/);
    if (!match) return null;
    // Exclude known non-agentId paths
    const nonAgentPaths = ["new", "recipes", "simulations"];
    return nonAgentPaths.includes(match[1]) ? null : match[1];
  }, [location.pathname]);

  // Extract current environmentId from URL search params
  const currentEnvironmentId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("e");
  }, [location.search]);

  // Convert agents to SearchItems
  const agentSearchItems: SearchItem[] = useMemo(() => {
    return agents.map((agent) => ({
      id: `agent-${agent.id}`,
      title: agent.name,
      description: agent.description || undefined,
      keywords: [agent.name.toLowerCase()],
      category: "Agents" as SearchCategory,
      path: `/agents/${agent.id}/overview`,
      icon: "bot",
    }));
  }, [agents]);

  // Search when query changes
  useEffect(() => {
    // If pending feature, only show agents
    if (pendingFeature) {
      const agentResults = query.trim() ? searchItems(agentSearchItems, query, isAdmin) : agentSearchItems.slice(0, 20).map((item) => ({ ...item, score: 0 }));
      setResults(agentResults);
      setSelectedIndex(0);
      return;
    }

    const staticResults = searchItems(searchRegistry, query, isAdmin);

    // Only show agents when there's a query
    const agentResults = query.trim() ? searchItems(agentSearchItems, query, isAdmin) : [];

    // Merge results, with agents first when there's a query match
    const allResults = [...agentResults, ...staticResults];

    setResults(allResults.slice(0, 20));
    setSelectedIndex(0);
  }, [query, isAdmin, agentSearchItems, pendingFeature]);

  // Reset and focus on open, fetch agents if needed
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setPendingFeature(null);
      setTimeout(() => inputRef.current?.focus(), 0);

      // Fetch agents if not already loaded
      if (!agentsLoaded) {
        dispatch(agentActions.get({}));
      }
    }
  }, [isOpen, agentsLoaded, dispatch]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedElement = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedElement?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  // Handle navigation to selected item
  const handleSelect = useCallback(
    (item: SearchResult) => {
      // If we have a pending feature and user selected an agent
      if (pendingFeature && item.category === "Agents") {
        const agentId = item.id.replace("agent-", "");
        let path = pendingFeature.path.replace(":agentId", agentId);
        close();
        navigate(path);
        return;
      }

      let path = item.path;

      // Replace :agentId placeholder with current agent or prompt for agent selection
      if (path.includes(":agentId")) {
        if (currentAgentId && currentAgentId !== "new") {
          path = path.replace(":agentId", currentAgentId);
        } else {
          // No agent selected - enter agent selection mode
          setPendingFeature(item);
          setQuery("");
          setSelectedIndex(0);
          return;
        }
      }

      close();

      // For agent settings that open modals, include environment ID if available
      if (item.category === "Agent Settings" && currentEnvironmentId) {
        // Check if path already has query params
        if (path.includes("?")) {
          path = `${path}&e=${currentEnvironmentId}`;
        } else {
          path = `${path}?e=${currentEnvironmentId}`;
        }
      }

      navigate(path);
    },
    [close, currentAgentId, currentEnvironmentId, navigate, pendingFeature],
  );

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case "Escape":
        // If in agent selection mode, go back; otherwise close
        if (pendingFeature) {
          setPendingFeature(null);
          setQuery("");
        } else {
          close();
        }
        break;
      case "Backspace":
        // If query is empty and in agent selection mode, go back
        if (pendingFeature && query === "") {
          e.preventDefault();
          setPendingFeature(null);
        }
        break;
    }
  };

  // Group results by category
  const groupedResults = useMemo(() => {
    const groups: Record<string, SearchResult[]> = {};
    results.forEach((item) => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    // Sort by category order
    return categoryOrder.filter((cat) => groups[cat]?.length > 0).map((cat) => ({ category: cat, items: groups[cat] }));
  }, [results]);

  // Calculate global index for each item
  const getGlobalIndex = (categoryIdx: number, itemIdx: number): number => {
    let index = 0;
    for (let i = 0; i < categoryIdx; i++) {
      index += groupedResults[i].items.length;
    }
    return index + itemIdx;
  };

  return (
    <>
      <HeadlessDialog
        open={isOpen}
        onClose={close}
        className="relative z-50"
      >
        {/* Backdrop */}
        <View
          className="fixed inset-0 bg-black/80"
          aria-hidden="true"
        />

        {/* Modal */}
        <View className="fixed inset-0 flex items-start justify-center pt-[15vh] p-4">
          <HeadlessDialog.Panel
            className={clsx(
              "w-full max-w-xl",
              "bg-white dark:bg-gray-800",
              "rounded-xl shadow-2xl",
              "border border-gray-200 dark:border-gray-700",
              "overflow-hidden",
            )}
          >
            {/* Search Input */}
            <View className="flex items-center px-4 border-b border-gray-200 dark:border-gray-700">
              <Icon
                name="search"
                size={20}
                className="text-gray-400 dark:text-gray-500 flex-shrink-0"
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={pendingFeature ? `Select agent for ${pendingFeature.title}...` : "Search pages, features, settings, and agents..."}
                className={clsx(
                  "w-full px-3 py-4",
                  "bg-transparent",
                  "text-gray-900 dark:text-gray-100",
                  "placeholder:text-gray-400 dark:placeholder:text-gray-500",
                  "focus:outline-none",
                  "text-base",
                )}
              />
              <View className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-[10px]">esc</kbd>
              </View>
            </View>

            {/* Results */}
            <View
              ref={resultsRef}
              className="max-h-[60vh] overflow-y-auto p-2"
            >
              {results.length === 0 && query && (
                <View className="px-4 py-8 text-center">
                  <Text className="text-gray-500 dark:text-gray-400">No results found for "{query}"</Text>
                </View>
              )}

              {groupedResults.map((group, groupIdx) => (
                <View
                  key={group.category}
                  className="mb-2"
                >
                  <Text className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{group.category}</Text>
                  {group.items.map((item, itemIdx) => {
                    const globalIdx = getGlobalIndex(groupIdx, itemIdx);
                    return (
                      <View
                        key={item.id}
                        data-index={globalIdx}
                      >
                        <CommandPaletteItem
                          item={item}
                          isSelected={globalIdx === selectedIndex}
                          onClick={() => handleSelect(item)}
                        />
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>

            {/* Footer hint */}
            <View className="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <View className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px]">↑</kbd>
                  <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px]">↓</kbd>
                  <span className="ml-1">navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-[10px]">↵</kbd>
                  <span className="ml-1">select</span>
                </span>
              </View>
            </View>
          </HeadlessDialog.Panel>
        </View>
      </HeadlessDialog>
    </>
  );
};
